/**
 * Zustand Layer State Store
 * 
 * Global state management for UI layer control using Zustand.
 * Provides centralized control over rendering, interaction, and display
 * of all UI layers with preset-based state management.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  LayerStore, 
  LayerActivationMap, 
  LayerPresets, 
  PresetId, 
  LayerState,
  DEFAULT_LAYER_STATE,
  INACTIVE_LAYER_STATE,
  DISABLED_LAYER_STATE,
  // HIDDEN_LAYER_STATE
} from '../types/layerState';
import { LayerId } from '../styles/styles-data/zIndex';

/**
 * Predefined layer state presets for common UI scenarios
 */
const LAYER_PRESETS: LayerPresets = {
  DEFAULT: {
    base: DEFAULT_LAYER_STATE,
    background: DEFAULT_LAYER_STATE,
    mainContent: DEFAULT_LAYER_STATE,
    navigation: DEFAULT_LAYER_STATE,
    overlays: INACTIVE_LAYER_STATE,
    accountApi: INACTIVE_LAYER_STATE,
    systemUI: INACTIVE_LAYER_STATE,
  },
  
  OVERLAY_ACTIVE: {
    base: DEFAULT_LAYER_STATE,
    background: DEFAULT_LAYER_STATE,
    mainContent: DISABLED_LAYER_STATE, // Visible but non-interactive
    navigation: DISABLED_LAYER_STATE,  // Visible but non-interactive
    overlays: DEFAULT_LAYER_STATE,     // Active and interactive
    accountApi: INACTIVE_LAYER_STATE,
    systemUI: INACTIVE_LAYER_STATE,
  },
  
  MOBILE_MENU_ACTIVE: {
    base: DEFAULT_LAYER_STATE,
    background: DEFAULT_LAYER_STATE,
    mainContent: DISABLED_LAYER_STATE, // Visible but non-interactive (allows scrolling to sections)
    navigation: DEFAULT_LAYER_STATE,   // Active and interactive
    overlays: INACTIVE_LAYER_STATE,
    accountApi: INACTIVE_LAYER_STATE,
    systemUI: INACTIVE_LAYER_STATE,
  },
  
  LOADING: {
    base: DEFAULT_LAYER_STATE,
    background: DEFAULT_LAYER_STATE,
    mainContent: DISABLED_LAYER_STATE, // Visible but non-interactive
    navigation: DISABLED_LAYER_STATE,  // Visible but non-interactive
    overlays: INACTIVE_LAYER_STATE,
    accountApi: INACTIVE_LAYER_STATE,
    systemUI: DEFAULT_LAYER_STATE,     // Loading indicators active
  },
};

/**
 * Initial layer state - all layers active by default
 */
const INITIAL_LAYER_STATE: LayerActivationMap = LAYER_PRESETS.DEFAULT;

/**
 * Create the Zustand store with subscriptions for debugging
 */
export const useLayerStore = create<LayerStore>()(
  subscribeWithSelector((set, get) => ({
    layerActivation: INITIAL_LAYER_STATE,
    currentPreset: 'DEFAULT',

    applyPreset: (presetId: PresetId) => {
      const preset = LAYER_PRESETS[presetId];
      if (!preset) {
        console.warn(`Unknown preset: ${presetId}`);
        return;
      }
      
      set({ 
        layerActivation: { ...preset },
        currentPreset: presetId 
      });
      
      // Debug logging in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`[LayerStore] Applied preset: ${presetId}`, preset);
      }
    },

    setLayerState: (layerId: LayerId, state: Partial<LayerState>) => {
      set((currentState) => ({
        layerActivation: {
          ...currentState.layerActivation,
          [layerId]: {
            ...currentState.layerActivation[layerId],
            ...state,
          },
        },
        // Reset preset to custom when manually modifying layers
        currentPreset: 'DEFAULT',
      }));
      
      // Debug logging in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`[LayerStore] Updated layer ${layerId}:`, state);
      }
    },

    getLayerState: (layerId: LayerId) => {
      return get().layerActivation[layerId] || DEFAULT_LAYER_STATE;
    },

    activateLayer: (layerId: LayerId) => {
      get().setLayerState(layerId, {
        rendered: true,
        interactive: true,
        displayed: true,
      });
    },

    deactivateLayer: (layerId: LayerId) => {
      get().setLayerState(layerId, {
        rendered: false,
        interactive: false,
        displayed: false,
      });
    },

    disableLayerInteraction: (layerId: LayerId) => {
      get().setLayerState(layerId, {
        interactive: false,
      });
    },

    hideLayer: (layerId: LayerId) => {
      get().setLayerState(layerId, {
        displayed: false,
      });
    },

    showLayer: (layerId: LayerId) => {
      get().setLayerState(layerId, {
        displayed: true,
      });
    },
  }))
);

/**
 * Development-only store subscription for debugging
 */
if (process.env.NODE_ENV === 'development') {
  useLayerStore.subscribe(
    (state) => state.currentPreset,
    (currentPreset, previousPreset) => {
      if (currentPreset !== previousPreset) {
        console.log(`[LayerStore] Preset changed: ${previousPreset} -> ${currentPreset}`);
      }
    }
  );

  useLayerStore.subscribe(
    (state) => state.layerActivation,
    (layerActivation, previousLayerActivation) => {
      // Find which layers changed
      const changedLayers: string[] = [];
      Object.keys(layerActivation).forEach((layerId) => {
        const layer = layerId as LayerId;
        const current = layerActivation[layer];
        const previous = previousLayerActivation?.[layer];
        
        if (JSON.stringify(current) !== JSON.stringify(previous)) {
          changedLayers.push(layer);
        }
      });
      
      if (changedLayers.length > 0) {
        console.log(`[LayerStore] Layer changes detected:`, changedLayers);
      }
    }
  );
}

/**
 * Get available presets
 */
export const getAvailablePresets = (): PresetId[] => {
  return Object.keys(LAYER_PRESETS) as PresetId[];
};

/**
 * Get preset definition
 */
export const getPreset = (presetId: PresetId): LayerActivationMap | undefined => {
  return LAYER_PRESETS[presetId];
};

/**
 * Validate layer state
 */
export const isValidLayerState = (state: Partial<LayerState>): boolean => {
  const validKeys = ['rendered', 'interactive', 'displayed'];
  return Object.keys(state).every(key => validKeys.includes(key));
};

/**
 * Development helper to log current layer states
 */
export const debugLayerStates = (): void => {
  if (process.env.NODE_ENV === 'development') {
    const { layerActivation, currentPreset } = useLayerStore.getState();
    console.group(`[LayerDebug] Current Layer States (Preset: ${currentPreset})`);
    Object.entries(layerActivation).forEach(([layerId, state]) => {
      const status = state.rendered && state.interactive && state.displayed 
        ? 'ACTIVE' 
        : state.rendered && state.displayed 
        ? 'VISIBLE' 
        : state.rendered 
        ? 'MOUNTED' 
        : 'INACTIVE';
      console.log(`${layerId}: ${status}`, state);
    });
    console.groupEnd();
  }
};